import React, { useState, useMemo, useCallback } from 'react';
import { useDb } from './db/useDataService';
import * as ds from './db/dataService';

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  InboxOutlined,
  SearchOutlined,
  FilterOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  RightOutlined,
  DownOutlined,
  TeamOutlined,
  FileTextOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import {
  Button,
  Table,
  Tag,
  Space,
  Popconfirm,
  Tooltip,
  Input,
  Select,
  Typography,
  message,
  Radio,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useNavigate } from 'react-router-dom';

import CreateProductModal from './components/CreateProductModal';
import './ProductsPage.scss';
import {
  Product,
  ProductStatus,
  ProductAuthor,
  ProductTag,
  ProductStats,
  ProductPeriodStats,
  CreateProductFormValues,
} from './types';
import MainLayout from '../../shared/common/components/layouts/MainLayout';

const { Title } = Typography;

// --- Mock data ---

const mockAuthors: ProductAuthor[] = [
  { id: '1', name: 'Иван Петров', description: 'Эксперт по маркетингу' },
  { id: '2', name: 'Мария Сидорова', description: 'Бизнес-тренер' },
  { id: '3', name: 'Алексей Козлов', description: 'Специалист по продажам' },
];

const mockTags: ProductTag[] = [
  { id: '1', name: 'Основной', color: 'blue', categoryId: '1', categoryName: 'Тип' },
  { id: '2', name: 'Допродажа', color: 'green', categoryId: '1', categoryName: 'Тип' },
  { id: '3', name: 'Тест', color: 'orange', categoryId: '2', categoryName: 'Статус работы' },
  { id: '4', name: 'Масштаб', color: 'purple', categoryId: '2', categoryName: 'Статус работы' },
  { id: '5', name: 'Важное', color: 'red' },
];

export const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Курс «Основы маркетинга»',
    url: 'https://example.com/marketing',
    description: 'Комплексный курс по основам маркетинга для начинающих',
    tasks: 'Обучение основам маркетинга',
    keyIdea: 'Системный подход к маркетингу с нуля',
    keyMeanings: 'Практика, результат, система',
    excludeWords: 'бесплатно, халява',
    keywords: 'маркетинг, продвижение, реклама',
    notes: '',
    comment: '',
    status: 'active',
    authorId: '1',
    author: mockAuthors[0],
    tags: [mockTags[0], mockTags[3]],
    offersCount: 3,
    createdAt: '2026-01-15T10:00:00Z',
    createdBy: 'user1',
  },
  {
    id: '2',
    name: 'Вебинар «Воронки продаж»',
    url: 'https://example.com/funnels-webinar',
    description: 'Бесплатный вебинар о построении воронок продаж',
    tasks: 'Привлечение лидов через вебинар',
    keyIdea: 'Автоматизация продаж через воронки',
    keyMeanings: 'Автоматизация, конверсия, доход',
    excludeWords: '',
    keywords: 'воронка, продажи, автоматизация',
    notes: 'Запуск в марте',
    comment: '',
    status: 'draft',
    authorId: '2',
    author: mockAuthors[1],
    tags: [mockTags[2]],
    offersCount: 1,
    createdAt: '2026-02-20T14:30:00Z',
    createdBy: 'user1',
  },
  {
    id: '3',
    name: 'Консультация по стратегии',
    url: 'https://example.com/consultation',
    description: 'Индивидуальная консультация по маркетинговой стратегии',
    tasks: 'Помощь в разработке стратегии',
    keyIdea: 'Персональный разбор бизнеса',
    keyMeanings: 'Индивидуальный подход, стратегия',
    excludeWords: '',
    keywords: 'консультация, стратегия, разбор',
    notes: '',
    comment: '',
    status: 'active',
    authorId: '3',
    author: mockAuthors[2],
    tags: [mockTags[0], mockTags[4]],
    offersCount: 2,
    createdAt: '2026-03-01T09:00:00Z',
    createdBy: 'user1',
  },
  {
    id: '4',
    name: 'Мини-курс «Email-маркетинг»',
    url: 'https://example.com/email-course',
    description: 'Краткий курс по email-маркетингу',
    tasks: 'Обучение email-рассылкам',
    keyIdea: 'Продажи через email без спама',
    keyMeanings: 'Email, рассылки, конверсия',
    excludeWords: 'спам',
    keywords: 'email, рассылка, письма',
    notes: '',
    comment: '',
    status: 'stopped',
    authorId: '1',
    author: mockAuthors[0],
    tags: [mockTags[1]],
    offersCount: 1,
    createdAt: '2026-02-10T11:00:00Z',
    createdBy: 'user1',
  },
  {
    id: '5',
    name: 'Курс «Таргетированная реклама»',
    url: 'https://example.com/targeting',
    description: 'Продвинутый курс по настройке таргетированной рекламы',
    tasks: 'Обучение настройке рекламы в ВК и Яндекс',
    keyIdea: 'Окупаемая реклама с первого запуска',
    keyMeanings: 'Таргет, ROI, оптимизация',
    excludeWords: '',
    keywords: 'таргет, реклама, ВК, Яндекс',
    notes: '',
    comment: 'Планируется запуск в апреле',
    status: 'archived',
    authorId: '2',
    author: mockAuthors[1],
    tags: [],
    offersCount: 0,
    createdAt: '2025-12-05T16:00:00Z',
    createdBy: 'user1',
  },
  {
    id: '10',
    name: 'Курс «Энергия мысли. Открой себя»',
    url: 'https://donskoj.info/otkrojsebja-online',
    description: 'Трансформационный online-курс Анатолия Донского. 21 день практики: перепрограммирование мышления, оздоровление, медитации',
    tasks: 'Привлечение через бесплатный вебинар, продажа курса',
    keyIdea: 'Мысль определяет здоровье, отношения и доход — научитесь управлять ей',
    keyMeanings: 'Сила мысли, оздоровление, трансформация, альфа-состояние, медитации',
    excludeWords: '',
    keywords: 'энергия мысли, Донской, оздоровление, медитация, подсознание',
    notes: '',
    comment: '',
    status: 'active',
    authorId: null,
    author: null,
    tags: [],
    offersCount: 2,
    createdAt: '2026-04-01T10:00:00Z',
    createdBy: 'user1',
  },
];

// --- Mock stats ---

const mockProductStats: Record<string, ProductStats> = {
  '1': {
    activeCampaigns: 3,
    activeAds: 12,
    dailyBudget: 5000,
    periods: {
      '7d': { spend: 35000, spendChange: 8.5, profit: 94000, profitChange: 12.4, romi: 280, romiChange: 5.2, cpl: 920, cplChange: -3.5 },
      '14d': { spend: 61000, spendChange: 12.3, profit: 189000, profitChange: 18.2, romi: 310, romiChange: 8.1, cpl: 880, cplChange: -5.2 },
      '30d': { spend: 85000, spendChange: 15.7, profit: 290000, profitChange: 22.5, romi: 340, romiChange: 11.3, cpl: 840, cplChange: -7.8 },
      all: { spend: 145000, spendChange: 0, profit: 319000, profitChange: 0, romi: 320, romiChange: 0, cpl: 850, cplChange: 0 },
    },
  },
  '2': {
    activeCampaigns: 1,
    activeAds: 4,
    dailyBudget: 2000,
    periods: {
      '7d': { spend: 14000, spendChange: 5.1, profit: 28000, profitChange: 9.3, romi: 200, romiChange: 4.0, cpl: 1100, cplChange: -2.1 },
      '14d': { spend: 25000, spendChange: 7.8, profit: 52000, profitChange: 11.5, romi: 210, romiChange: 5.2, cpl: 1050, cplChange: -3.8 },
      '30d': { spend: 42000, spendChange: 10.2, profit: 95000, profitChange: 15.1, romi: 226, romiChange: 7.1, cpl: 980, cplChange: -5.5 },
      all: { spend: 68000, spendChange: 0, profit: 140000, profitChange: 0, romi: 206, romiChange: 0, cpl: 1020, cplChange: 0 },
    },
  },
  '3': {
    activeCampaigns: 2,
    activeAds: 8,
    dailyBudget: 3500,
    periods: {
      '7d': { spend: 24500, spendChange: 6.2, profit: 73500, profitChange: 14.8, romi: 300, romiChange: 8.1, cpl: 780, cplChange: -4.2 },
      '14d': { spend: 44000, spendChange: 9.5, profit: 143000, profitChange: 19.7, romi: 325, romiChange: 9.4, cpl: 740, cplChange: -6.1 },
      '30d': { spend: 68000, spendChange: 13.1, profit: 238000, profitChange: 24.3, romi: 350, romiChange: 12.5, cpl: 700, cplChange: -8.9 },
      all: { spend: 112000, spendChange: 0, profit: 280000, profitChange: 0, romi: 330, romiChange: 0, cpl: 720, cplChange: 0 },
    },
  },
  '4': {
    activeCampaigns: 0,
    activeAds: 0,
    dailyBudget: 0,
    periods: {
      '7d': { spend: 0, spendChange: 0, profit: 0, profitChange: 0, romi: 0, romiChange: 0, cpl: 0, cplChange: 0 },
      '14d': { spend: 0, spendChange: 0, profit: 0, profitChange: 0, romi: 0, romiChange: 0, cpl: 0, cplChange: 0 },
      '30d': { spend: 8500, spendChange: -100, profit: 12000, profitChange: -100, romi: 141, romiChange: -100, cpl: 1200, cplChange: 0 },
      all: { spend: 35000, spendChange: 0, profit: 52000, profitChange: 0, romi: 149, romiChange: 0, cpl: 1150, cplChange: 0 },
    },
  },
  '5': {
    activeCampaigns: 0,
    activeAds: 0,
    dailyBudget: 0,
    periods: {
      '7d': { spend: 0, spendChange: 0, profit: 0, profitChange: 0, romi: 0, romiChange: 0, cpl: 0, cplChange: 0 },
      '14d': { spend: 0, spendChange: 0, profit: 0, profitChange: 0, romi: 0, romiChange: 0, cpl: 0, cplChange: 0 },
      '30d': { spend: 0, spendChange: 0, profit: 0, profitChange: 0, romi: 0, romiChange: 0, cpl: 0, cplChange: 0 },
      all: { spend: 92000, spendChange: 0, profit: 185000, profitChange: 0, romi: 201, romiChange: 0, cpl: 960, cplChange: 0 },
    },
  },
  '10': {
    activeCampaigns: 5,
    activeAds: 22,
    dailyBudget: 8000,
    periods: {
      '7d': { spend: 56000, spendChange: 11.2, profit: 168000, profitChange: 18.5, romi: 300, romiChange: 6.8, cpl: 650, cplChange: -5.1 },
      '14d': { spend: 98000, spendChange: 14.7, profit: 312000, profitChange: 22.1, romi: 318, romiChange: 8.9, cpl: 620, cplChange: -7.3 },
      '30d': { spend: 148000, spendChange: 18.3, profit: 510000, profitChange: 28.7, romi: 345, romiChange: 12.1, cpl: 580, cplChange: -9.8 },
      all: { spend: 210000, spendChange: 0, profit: 620000, profitChange: 0, romi: 295, romiChange: 0, cpl: 610, cplChange: 0 },
    },
  },
};

// --- Helpers ---

const formatMoney = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M ₽`;
  if (value >= 1000) return `${Math.round(value / 1000).toLocaleString('ru-RU')} 000 ₽`;
  return `${value.toLocaleString('ru-RU')} ₽`;
};

const formatChange = (value: number): React.ReactNode => {
  if (value === 0) return null;
  const isPositive = value > 0;
  return (
    <span className={`stat-change ${isPositive ? 'stat-change--positive' : 'stat-change--negative'}`}>
      {isPositive ? '+' : ''}{value}%
    </span>
  );
};

const PERIOD_LABELS: Record<string, string> = {
  '7d': '7 дней',
  '14d': '14 дней',
  '30d': '30 дней',
  all: 'Всё время',
};

// --- Status config ---

const STATUS_CONFIG: Record<ProductStatus, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'default' },
  active: { label: 'Запущено', color: 'green' },
  stopped: { label: 'Остановлено', color: 'orange' },
  archived: { label: 'Архив', color: 'red' },
};

// --- Component ---

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const db = useDb();
  const products = db.products;
  const setProducts: React.Dispatch<React.SetStateAction<Product[]>> = (u) => {
    const next = typeof u === 'function' ? (u as (p: Product[]) => Product[])(products) : u;
    ds.setProducts(next);
  };
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<ProductStatus | 'all'>('all');
  const [filterAuthor, setFilterAuthor] = useState<string | 'all'>('all');
  const [filterTag, setFilterTag] = useState<string | 'all'>('all');
  const [filtersVisible, setFiltersVisible] = useState(false);

  const toggleCardExpanded = useCallback((id: string) => {
    setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      if (searchText && !product.name.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }
      if (filterStatus !== 'all' && product.status !== filterStatus) {
        return false;
      }
      if (filterAuthor !== 'all' && product.authorId !== filterAuthor) {
        return false;
      }
      if (filterTag !== 'all' && !product.tags.some((tag) => tag.id === filterTag)) {
        return false;
      }
      return true;
    });
  }, [products, searchText, filterStatus, filterAuthor, filterTag]);

  const handleCreateProduct = useCallback((values: CreateProductFormValues) => {
    setModalLoading(true);
    setTimeout(() => {
      const newProduct: Product = {
        id: String(Date.now()),
        name: values.name,
        url: values.url,
        description: '',
        tasks: '',
        keyIdea: '',
        keyMeanings: '',
        excludeWords: '',
        keywords: '',
        notes: '',
        comment: '',
        status: 'draft',
        authorId: null,
        author: null,
        tags: [],
        offersCount: 0,
        createdAt: new Date().toISOString(),
        createdBy: 'user1',
      };
      setProducts((prev) => [newProduct, ...prev]);
      setModalVisible(false);
      setModalLoading(false);
      message.success('Продукт создан');
    }, 500);
  }, []);

  const handleArchiveProduct = useCallback((id: string) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status: 'archived' as ProductStatus } : p))
    );
    message.success('Продукт архивирован');
  }, []);

  const handleDeleteProduct = useCallback((id: string) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
    message.success('Продукт удалён');
  }, []);

  const handleDuplicateProduct = useCallback((product: Product) => {
    const duplicate: Product = {
      ...product,
      id: String(Date.now()),
      name: `${product.name} (копия)`,
      status: 'draft',
      offersCount: 0,
      createdAt: new Date().toISOString(),
    };
    setProducts((prev) => [duplicate, ...prev]);
    message.success('Продукт дублирован');
  }, []);

  const resetFilters = useCallback(() => {
    setSearchText('');
    setFilterStatus('all');
    setFilterAuthor('all');
    setFilterTag('all');
  }, []);

  const hasActiveFilters = filterStatus !== 'all' || filterAuthor !== 'all' || filterTag !== 'all' || searchText !== '';

  const columns: ColumnsType<Product> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record: Product) => (
        <div>
          <a
            className="products-page__product-link"
            href={`/products/${record.id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/products/${record.id}`);
            }}
          >
            {name}
          </a>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>ID: {record.id}</div>
        </div>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: ProductStatus) => {
        const config = STATUS_CONFIG[status];
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: 'Кампании',
      key: 'campaigns',
      width: 100,
      align: 'center' as const,
      sorter: (a, b) => (mockProductStats[a.id]?.activeCampaigns || 0) - (mockProductStats[b.id]?.activeCampaigns || 0),
      render: (_: unknown, record: Product) => {
        const stats = mockProductStats[record.id];
        return <span style={{ fontWeight: 600 }}>{stats?.activeCampaigns ?? '—'}</span>;
      },
    },
    {
      title: 'Объявления',
      key: 'ads',
      width: 110,
      align: 'center' as const,
      sorter: (a, b) => (mockProductStats[a.id]?.activeAds || 0) - (mockProductStats[b.id]?.activeAds || 0),
      render: (_: unknown, record: Product) => {
        const stats = mockProductStats[record.id];
        return <span style={{ fontWeight: 600 }}>{stats?.activeAds ?? '—'}</span>;
      },
    },
    {
      title: 'Бюджет/день',
      key: 'budget',
      width: 120,
      align: 'right' as const,
      sorter: (a, b) => (mockProductStats[a.id]?.dailyBudget || 0) - (mockProductStats[b.id]?.dailyBudget || 0),
      render: (_: unknown, record: Product) => {
        const stats = mockProductStats[record.id];
        if (!stats || stats.dailyBudget === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return <span style={{ fontWeight: 600 }}>{stats.dailyBudget.toLocaleString('ru-RU')} ₽</span>;
      },
    },
    {
      title: 'Расход (30д)',
      key: 'spend',
      width: 130,
      align: 'right' as const,
      sorter: (a, b) => (mockProductStats[a.id]?.periods['30d'].spend || 0) - (mockProductStats[b.id]?.periods['30d'].spend || 0),
      render: (_: unknown, record: Product) => {
        const stats = mockProductStats[record.id];
        if (!stats || stats.periods['30d'].spend === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
        const p = stats.periods['30d'];
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{formatMoney(p.spend)}</div>
            {formatChange(p.spendChange)}
          </div>
        );
      },
    },
    {
      title: 'ROMI (30д)',
      key: 'romi',
      width: 110,
      align: 'right' as const,
      sorter: (a, b) => (mockProductStats[a.id]?.periods['30d'].romi || 0) - (mockProductStats[b.id]?.periods['30d'].romi || 0),
      render: (_: unknown, record: Product) => {
        const stats = mockProductStats[record.id];
        if (!stats || stats.periods['30d'].romi === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
        const p = stats.periods['30d'];
        return (
          <div>
            <div style={{ fontWeight: 700, color: p.romi >= 200 ? '#52c41a' : '#faad14' }}>+{p.romi}%</div>
            {formatChange(p.romiChange)}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_: unknown, record: Product) => (
        <Space size={4}>
          <Tooltip title="Редактировать">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/products/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Дублировать">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleDuplicateProduct(record)}
            />
          </Tooltip>
          {record.status !== 'archived' && (
            <Tooltip title="Архивировать">
              <Popconfirm
                title="Архивировать продукт?"
                onConfirm={() => handleArchiveProduct(record.id)}
                okText="Да"
                cancelText="Нет"
              >
                <Button type="text" size="small" icon={<InboxOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
          <Tooltip title="Удалить">
            <Popconfirm
              title="Удалить продукт? Это действие нельзя отменить"
              onConfirm={() => handleDeleteProduct(record.id)}
              okText="Да"
              cancelText="Нет"
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // --- Card stats renderer ---
  const renderStatsBlock = (stats: ProductStats) => {
    const metricRows: Array<{ label: string; key: keyof ProductPeriodStats }> = [
      { label: 'Расход', key: 'spend' },
      { label: 'Прибыль', key: 'profit' },
      { label: 'ROMI', key: 'romi' },
      { label: 'CPL', key: 'cpl' },
    ];
    const periodKeys = ['7d', '14d', '30d', 'all'] as const;

    return (
      <div className="product-card__stats">
        <div className="product-card__stats-table">
          <div className="product-card__stats-header">
            <div className="product-card__stats-label" />
            {periodKeys.map((pk) => (
              <div key={pk} className="product-card__stats-period">{PERIOD_LABELS[pk]}</div>
            ))}
          </div>
          {metricRows.map((row) => (
            <div key={row.key} className="product-card__stats-row">
              <div className="product-card__stats-label">{row.label}</div>
              {periodKeys.map((pk) => {
                const val = stats.periods[pk][row.key];
                const changeKey = (row.key + 'Change') as keyof ProductPeriodStats;
                const change = stats.periods[pk][changeKey];
                const isRomi = row.key === 'romi';
                const isMoney = row.key === 'spend' || row.key === 'profit' || row.key === 'cpl';
                return (
                  <div key={pk} className="product-card__stats-cell">
                    {pk !== 'all' && change !== 0 && formatChange(change)}
                    <span className={`product-card__stats-value ${isRomi ? 'product-card__stats-value--accent' : ''}`}>
                      {isRomi ? `+${val}%` : isMoney ? formatMoney(val) : val}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderProductCard = (product: Product) => {
    const stats = mockProductStats[product.id];
    const isExpanded = expandedCards[product.id] || false;
    const statusConfig = STATUS_CONFIG[product.status] || { label: product.status || '—', color: 'default' };
    const tags = product.tags || [];

    return (
      <div key={product.id} className={`product-card ${isExpanded ? 'product-card--expanded' : ''}`}>
        <div className="product-card__header" onClick={() => toggleCardExpanded(product.id)}>
          <div className="product-card__header-left">
            <span className="product-card__expand-icon">
              {isExpanded ? <DownOutlined /> : <RightOutlined />}
            </span>
            <div className="product-card__info">
              <a
                className="product-card__name"
                href={`/products/${product.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/products/${product.id}`);
                }}
              >
                {product.name}
              </a>
              <div className="product-card__meta">
                <span className="product-card__id">ID: {product.id}</span>
                <Tag color={statusConfig.color}>{statusConfig.label}</Tag>
                {tags.map((tag) => (
                  <Tag key={tag.id} color={tag.color}>{tag.name}</Tag>
                ))}
              </div>
            </div>
          </div>
          <div className="product-card__header-right">
            {stats && (
              <div className="product-card__summary">
                <div className="product-card__summary-item">
                  <TeamOutlined />
                  <span className="product-card__summary-value">{stats.activeCampaigns}</span>
                  <span className="product-card__summary-label">кампаний</span>
                </div>
                <div className="product-card__summary-item">
                  <FileTextOutlined />
                  <span className="product-card__summary-value">{stats.activeAds}</span>
                  <span className="product-card__summary-label">объявл.</span>
                </div>
                <div className="product-card__summary-item">
                  <DollarOutlined />
                  <span className="product-card__summary-value">
                    {stats.dailyBudget > 0 ? `${stats.dailyBudget.toLocaleString('ru-RU')} ₽` : '—'}
                  </span>
                  <span className="product-card__summary-label">в день</span>
                </div>
              </div>
            )}
            <div className="product-card__actions" onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Редактировать">
                <Button type="text" size="small" icon={<EditOutlined />} onClick={() => navigate(`/products/${product.id}`)} />
              </Tooltip>
              <Tooltip title="Дублировать">
                <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleDuplicateProduct(product)} />
              </Tooltip>
              {product.status !== 'archived' && (
                <Popconfirm title="Архивировать продукт?" onConfirm={() => handleArchiveProduct(product.id)} okText="Да" cancelText="Нет">
                  <Button type="text" size="small" icon={<InboxOutlined />} />
                </Popconfirm>
              )}
              <Popconfirm title="Удалить продукт?" onConfirm={() => handleDeleteProduct(product.id)} okText="Да" cancelText="Нет">
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>
          </div>
        </div>
        {isExpanded && stats && renderStatsBlock(stats)}
      </div>
    );
  };

  return (
    <MainLayout pageTitle="Продукты">
      <div className="page-wrapper">
        <div className="page-content">
          <div className="products-page">
            <div className="products-page__header">
          <div className="products-page__header-left">
            <Title level={4} className="products-page__title">
              Продукты
            </Title>
          </div>
          <div className="products-page__header-actions">
            <Radio.Group
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              size="middle"
              className="products-page__view-toggle"
            >
              <Radio.Button value="cards">
                <AppstoreOutlined />
              </Radio.Button>
              <Radio.Button value="table">
                <UnorderedListOutlined />
              </Radio.Button>
            </Radio.Group>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="products-page__create-btn"
              onClick={() => setModalVisible(true)}
            >
              Создать продукт
            </Button>
          </div>
        </div>

        <div className="products-page__filters">
          <div className="products-page__filters-header" onClick={() => setFiltersVisible(!filtersVisible)}>
            <span className="products-page__filters-title">
              <FilterOutlined />
              Фильтры
              {hasActiveFilters && (
                <Tag color="blue" style={{ marginLeft: 8 }}>Активны</Tag>
              )}
            </span>
          </div>
          {filtersVisible && (
            <div className="products-page__filters-content">
              <div className="products-page__filters-row">
                <div className="products-page__filters-item">
                  <span className="products-page__filters-label">Поиск</span>
                  <Input
                    placeholder="Название продукта"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </div>
                <div className="products-page__filters-item">
                  <span className="products-page__filters-label">Статус</span>
                  <Select
                    value={filterStatus}
                    onChange={setFilterStatus}
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="all">Все статусы</Select.Option>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <Select.Option key={key} value={key}>
                        {config.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="products-page__filters-item">
                  <span className="products-page__filters-label">Автор</span>
                  <Select
                    value={filterAuthor}
                    onChange={setFilterAuthor}
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="all">Все авторы</Select.Option>
                    {mockAuthors.map((author) => (
                      <Select.Option key={author.id} value={author.id}>
                        {author.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="products-page__filters-item">
                  <span className="products-page__filters-label">Тег</span>
                  <Select
                    value={filterTag}
                    onChange={setFilterTag}
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="all">Все теги</Select.Option>
                    {mockTags.map((tag) => (
                      <Select.Option key={tag.id} value={tag.id}>
                        {tag.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
              {hasActiveFilters && (
                <div className="products-page__filters-actions">
                  <Button size="small" onClick={resetFilters}>
                    Сбросить фильтры
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {viewMode === 'table' ? (
          <div className="products-page__table">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredProducts}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `Всего: ${total}`,
              }}
              locale={{
                emptyText: 'Нет продуктов',
              }}
            />
          </div>
        ) : (
          <div className="products-page__cards">
            {filteredProducts.length === 0 ? (
              <div className="products-page__empty">Нет продуктов</div>
            ) : (
              filteredProducts.map((product) => renderProductCard(product))
            )}
          </div>
        )}

        <CreateProductModal
          visible={modalVisible}
          loading={modalLoading}
          onCancel={() => setModalVisible(false)}
          onSubmit={handleCreateProduct}
        />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ProductsPage;
